import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { formatNumber, formatDateTime } from '../lib/utils';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import Button from './ui/Button';
import { SkeletonRow } from './ui/Skeleton';

/**
 * T2.2 (tor 2, 18.10.2026): rozliczenia agencji.
 *  - raport zużycia za miesiąc per organizacja (RPC org_usage_report) + CSV do faktury,
 *  - plan organizacji (RPC set_org_plan): plan odnawialny dopełnia pulę od razu i potem co miesiąc (cron renew_org_plans).
 */

const thisMonth = () => new Date().toISOString().slice(0, 7);

const CSV_COLUMNS = [
  ['month', 'miesiąc'], ['org_name', 'organizacja'], ['plan', 'plan'], ['price_pln', 'abonament_pln_netto'],
  ['orders', 'zlecenia'], ['sku', 'sku'], ['credits_charged', 'kredyty_pobrane'], ['credits_refunded', 'kredyty_zwrócone'],
  ['credits_net', 'kredyty_netto'], ['credits_granted', 'kredyty_dopisane'], ['channels', 'kanały'], ['clients', 'klienci'],
  ['aplus_orders', 'zlecenia_A+'], ['balance_now', 'saldo_teraz'], ['org_id', 'org_id'],
];

export function usageToCsv(rows, plansByCode = {}) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = CSV_COLUMNS.map(c => c[1]).join(';');
  const body = rows.map(r => CSV_COLUMNS.map(([k]) => {
    if (k === 'price_pln') return esc(plansByCode[r.plan]?.price_pln != null ? String(plansByCode[r.plan].price_pln).replace('.', ',') : '');
    return esc(r[k]);
  }).join(';'));
  // BOM: Excel w polskich ustawieniach otwiera UTF-8 i średniki poprawnie.
  return '﻿' + [head, ...body].join('\r\n');
}

const usd = (n) => `${Number(n ?? 0).toFixed(2).replace('.', ',')} USD`;

/** Budżet AI na rozwój (RPC ai_budget_status / set_ai_budget, migracja 20261018200007): ile poszło, na co, limity. */
function AiBudgetCard() {
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [total, setTotal] = useState('');
  const [daily, setDaily] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('ai_budget_status');
    if (error) { setErr(error.message); return; }
    setErr(null); setB(data);
    setTotal(String(data?.budget_usd ?? '')); setDaily(String(data?.daily_usd ?? ''));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return null; // RPC jeszcze nie wgrane na tym projekcie — karta się nie pokazuje.
  if (!b) return <Card><CardBody><SkeletonRow /></CardBody></Card>;

  const save = async () => {
    if (!String(total).trim() || !String(daily).trim()) return toast.error('Wpisz obie kwoty w USD (limit dzienny 0 = bez limitu).');
    const t = Number(String(total).replace(',', '.')); const d = Number(String(daily).replace(',', '.'));
    if (!(t >= 0) || !(d >= 0)) return toast.error('Podaj kwoty w USD (limit dzienny 0 = bez limitu).');
    if (t === 0 && !window.confirm('Budżet całkowity 0 = BEZ LIMITU (blokada wyłączona). Na pewno?')) return;
    setSaving(true);
    const { error } = await supabase.rpc('set_ai_budget', { p_total: t, p_daily: d });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success('Budżet AI zapisany');
    load();
  };

  const pct = Math.min(100, Number(b.pct) || 0);
  const bar = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-brand-600';
  const blockedTotal = b.hard_stop && Number(b.budget_usd) > 0 && Number(b.spent_usd) >= Number(b.budget_usd);
  const blockedDay = b.hard_stop && Number(b.daily_usd) > 0 && Number(b.today_usd) >= Number(b.daily_usd);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Budżet AI</CardTitle>
        <CardDescription>
          Koszt modeli od {formatDateTime(b.since)} (silnik + koszt prod {usd(b.offset_usd)}). Maile przy 50/80/100% i po przekroczeniu limitu dziennego.
          {b.hard_stop ? ' Po przekroczeniu nowe zlecenia są wstrzymywane (zlecenia w toku kończą się).' : ' Blokada wyłączona — tylko powiadomienia.'}
        </CardDescription>
      </CardHeader>
      <CardBody className="grid gap-4">
        <div>
          <div className="flex flex-wrap justify-between gap-2 text-sm mb-1.5">
            <span><b>{usd(b.spent_usd)}</b> z {usd(b.budget_usd)} ({b.pct}%) · zostało {usd(b.left_usd)}</span>
            <span>dziś <b>{usd(b.today_usd)}</b>{Number(b.daily_usd) > 0 ? ` z ${usd(b.daily_usd)}` : ''}</span>
          </div>
          <div className="h-2 rounded bg-ink-100 dark:bg-ink-800 overflow-hidden"><div className={`h-2 ${bar}`} style={{ width: `${pct}%` }} /></div>
          {(blockedTotal || blockedDay) && (
            <p className="mt-2 text-sm text-red-600">Nowe zlecenia wstrzymane: {blockedTotal ? 'budżet całkowity wykorzystany' : 'limit dzienny przekroczony (do północy)'}.</p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="grid gap-1">Budżet całkowity (USD)
            <input value={total} onChange={e => setTotal(e.target.value)} inputMode="decimal" className="h-9 w-32 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2" />
          </label>
          <label className="grid gap-1">Limit dzienny (USD, 0 = brak)
            <input value={daily} onChange={e => setDaily(e.target.value)} inputMode="decimal" className="h-9 w-32 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2" />
          </label>
          <Button size="sm" onClick={save} loading={saving}>Zapisz</Button>
          <Button size="sm" variant="ghost" onClick={load} leftIcon={<RefreshCw className="w-4 h-4" />}>Odśwież</Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 text-sm">
          <div>
            <div className="text-2xs uppercase tracking-wide text-ink-500 mb-1">Dni</div>
            {(b.by_day || []).slice(0, 10).map(d => (
              <div key={d.day} className="flex justify-between py-0.5"><span>{d.day}</span><span className="tabular-nums">{usd(d.usd)} · {d.orders} zl.</span></div>
            ))}
          </div>
          <div>
            <div className="text-2xs uppercase tracking-wide text-ink-500 mb-1">Najdroższe zlecenia</div>
            {(b.top_orders || []).slice(0, 8).map(o => (
              <div key={o.order_id} className="flex justify-between gap-2 py-0.5">
                <span className="truncate" title={o.order_id}>{o.filename || String(o.order_id).slice(0, 8)} <span className="text-ink-500">({o.products ?? '?'} prod.)</span></span>
                <span className="tabular-nums shrink-0">{usd(o.usd)}</span>
              </div>
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default function AdminBillingTab() {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState(null);
  const [plans, setPlans] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [busy, setBusy] = useState(null);

  const plansByCode = Object.fromEntries(plans.map(p => [p.code, p]));

  const load = useCallback(async () => {
    setRows(null);
    const [{ data: r, error }, { data: p }, { data: o }] = await Promise.all([
      supabase.rpc('org_usage_report', { p_month: `${month}-01` }),
      supabase.from('plans').select('*').order('sort'),
      supabase.from('organizations').select('id, name, plan, credits, renews_at').order('name'),
    ]);
    if (error) { toast.error(`Raport: ${error.message}`); setRows([]); } else setRows(r || []);
    setPlans(p || []);
    setOrgs(o || []);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const download = () => {
    const csv = usageToCsv(rows || [], plansByCode);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mams_zuzycie_${month}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const changePlan = async (org, plan) => {
    if (plan === org.plan) return;
    const p = plansByCode[plan];
    if (!window.confirm(`Zmienić plan „${org.name}” na ${p?.name || plan}?${p?.renews ? ` Pula zostanie od razu dopełniona do ${formatNumber(p.monthly_credits)} kredytów i będzie odnawiana co miesiąc.` : ''}`)) return;
    setBusy(org.id);
    const { data, error } = await supabase.rpc('set_org_plan', { p_org: org.id, p_plan: plan });
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success(`Plan ${p?.name || plan}: pula ${formatNumber(data?.credits ?? 0)}${data?.added ? ` (+${formatNumber(data.added)})` : ''}`);
    load();
  };

  const totals = (rows || []).reduce((t, r) => ({ sku: t.sku + (r.sku || 0), net: t.net + (r.credits_net || 0) }), { sku: 0, net: 0 });

  return (
    <div className="grid gap-5">
      <AiBudgetCard />
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle>Zużycie za miesiąc</CardTitle>
              <CardDescription>Per organizacja: zlecenia, SKU, kredyty (1 kredyt = 1 produkt × 1 kanał), kanały. CSV otwiera się w Excelu — podstawa do faktury.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-9 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 text-sm" />
              <Button size="sm" variant="secondary" onClick={load} leftIcon={<RefreshCw className="w-4 h-4" />}>Odśwież</Button>
              <Button size="sm" onClick={download} disabled={!rows?.length} leftIcon={<Download className="w-4 h-4" />}>Pobierz CSV</Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {rows == null ? <SkeletonRow /> : rows.length === 0 ? (
            <p className="text-sm text-ink-500">W tym miesiącu żadna organizacja nie zużyła kredytów.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-2xs uppercase tracking-wide text-ink-500">
                  <th className="py-2 pr-3">Organizacja</th><th className="pr-3">Plan</th><th className="pr-3 text-right">Zlecenia</th>
                  <th className="pr-3 text-right">SKU</th><th className="pr-3 text-right">Kredyty netto</th><th className="pr-3">Kanały</th>
                  <th className="pr-3 text-right">Klienci</th><th className="text-right">Saldo</th>
                </tr></thead>
                <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                  {rows.map(r => (
                    <tr key={r.org_id}>
                      <td className="py-2 pr-3 font-medium">{r.org_name}</td>
                      <td className="pr-3">{plansByCode[r.plan]?.name || r.plan}</td>
                      <td className="pr-3 text-right tabular-nums">{formatNumber(r.orders)}</td>
                      <td className="pr-3 text-right tabular-nums">{formatNumber(r.sku)}</td>
                      <td className="pr-3 text-right tabular-nums" title={`pobrane ${r.credits_charged}, zwrócone ${r.credits_refunded}`}>{formatNumber(r.credits_net)}</td>
                      <td className="pr-3 text-xs text-ink-600 dark:text-ink-300">{r.channels || '—'}</td>
                      <td className="pr-3 text-right tabular-nums">{r.clients}</td>
                      <td className="text-right tabular-nums">{r.plan === 'mediafy' ? '∞' : formatNumber(r.balance_now)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold"><td className="py-2">Razem</td><td /><td /><td className="text-right tabular-nums">{formatNumber(totals.sku)}</td><td className="text-right tabular-nums pr-3">{formatNumber(totals.net)}</td><td colSpan={3} /></tr>
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Plany organizacji</CardTitle>
          <CardDescription>Plan odnawialny dopełnia pulę do kwoty planu co miesiąc (od dnia zmiany planu). Niewykorzystane kredyty planu nie przechodzą; doładowania ponad plan zostają. Free = 20 kredytów jednorazowo.</CardDescription>
        </CardHeader>
        <CardBody>
          <div className="mb-4 flex flex-wrap gap-2 text-xs">
            {plans.filter(p => p.code !== 'mediafy').map(p => (
              <span key={p.code} className="rounded-md border border-ink-200 dark:border-ink-700 px-2 py-1">
                <b>{p.name}</b> · {formatNumber(p.monthly_credits)} kr{p.renews ? '/mies.' : ' jednorazowo'} · {formatNumber(p.price_pln)} zł{p.overage_pln ? ` · ponad pulę ${String(p.overage_pln).replace('.', ',')} zł` : ''}
              </span>
            ))}
          </div>
          <div className="divide-y divide-ink-100 dark:divide-ink-800 text-sm">
            {orgs.map(o => (
              <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="font-medium">{o.name}</span>
                <span className="flex items-center gap-3">
                  <span className="text-xs text-ink-500 tabular-nums">{o.plan === 'mediafy' ? 'bez limitu' : `${formatNumber(o.credits)} kr`}{o.renews_at ? ` · odnowienie ${formatDateTime(o.renews_at)}` : ''}</span>
                  {o.plan === 'mediafy' ? <span className="text-xs text-ink-400">Mediafy</span> : (
                    <select value={o.plan} disabled={busy === o.id} onChange={e => changePlan(o, e.target.value)}
                      className="h-8 rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 text-sm">
                      {plans.filter(p => p.code !== 'mediafy' && p.active).map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                    </select>
                  )}
                </span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
